import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Textarea } from '../ui/textarea.tsx';
import { Plus, Edit, Trash2, X, Calendar, Users } from 'lucide-react';

interface AdminEvent { 
    id: number; 
    Title: string; 
    Description: string; 
    StartDate: string; 
    EndDate: string; 
    Location: string; 
    attendees_count?: number; 
    Image?: string | null; 
}

interface EventsTabProps {
    events: AdminEvent[];
    showAddForm: string | null;
    setShowAddForm: (value: string | null) => void;
    newEvent: {
        title: string;
        description: string;
        startDate: string;
        endDate: string;
        location: string;
    };
    setNewEvent: (value: any) => void;
    selectedFile: File | null;
    setSelectedFile: (file: File | null) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSaveEvent: (e: React.FormEvent) => Promise<void>;
    resetEventForm: () => void;
    handleEditEvent: (evt: AdminEvent) => void;
    handleDeleteEvent: (id: number) => Promise<void>;
    handleViewAttendees: (eventId: number, eventTitle: string) => Promise<void>;
    editingId: number | null;
}

export function EventsTab({
    events,
    showAddForm,
    setShowAddForm,
    newEvent,
    setNewEvent,
    selectedFile,
    setSelectedFile,
    handleFileChange,
    handleSaveEvent,
    resetEventForm,
    handleEditEvent,
    handleDeleteEvent,
    handleViewAttendees,
    editingId
}: EventsTabProps) {
    return (
        <Card>
            <CardHeader className="flex flex-row justify-between">
                <CardTitle>Etkinlikler</CardTitle>
                <Button 
                    onClick={() => { 
                        resetEventForm(); 
                        setShowAddForm('event'); 
                    }} 
                    className="gap-2"
                >
                    <Plus className="h-4 w-4"/> Yeni
                </Button>
            </CardHeader>
            <CardContent>
                {showAddForm === 'event' && (
                    <Card className="bg-slate-50 mb-4 border-2 border-blue-200">
                        <CardContent className="pt-6">
                            <form onSubmit={handleSaveEvent} className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Başlık *</Label>
                                        <Input 
                                            value={newEvent.title} 
                                            onChange={e => setNewEvent({...newEvent, title: e.target.value})} 
                                            required 
                                        />
                                    </div>
                                    <div className="space-y-2">
                                    <Label className="font-semibold text-slate-700">Google Maps Konum Linki *</Label>
                                    <Input 
                                        type="url"
                                        placeholder="https://maps.app.goo.gl/... veya https://google.com/maps/..." 
                                        value={newEvent.location} 
                                        onChange={e => setNewEvent({...newEvent, location: e.target.value})} 
                                        required 
                                    />
                                    <p className="text-xs text-slate-500 italic">
                                        * Google Maps uygulamasından veya sitesinden etkinlik konumunu bulun. 
                                        "Paylaş" butonuna basıp "Bağlantıyı kopyala" dedikten sonra buraya yapıştırın.
                                    </p>
                                </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Açıklama</Label>
                                    <Textarea 
                                        value={newEvent.description} 
                                        onChange={e => setNewEvent({...newEvent, description: e.target.value})} 
                                    />
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Başlangıç Tarihi ve Saati</Label>
                                        <Input 
                                            type="datetime-local" 
                                            value={newEvent.startDate} 
                                            onChange={e => setNewEvent({...newEvent, startDate: e.target.value})} 
                                            required 
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bitiş Tarihi ve Saati (Opsiyonel)</Label>
                                        <Input 
                                            type="datetime-local" 
                                            value={newEvent.endDate} 
                                            onChange={e => setNewEvent({...newEvent, endDate: e.target.value})} 
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Görsel</Label>
                                    <div className="flex items-center gap-2">
                                        <Input 
                                            type="file" 
                                            accept="image/*" 
                                            onChange={handleFileChange} 
                                            className="bg-white"
                                        />
                                        {selectedFile && (
                                            <Button 
                                                type="button" 
                                                size="icon" 
                                                variant="ghost" 
                                                onClick={() => setSelectedFile(null)}
                                            >
                                                <X className="h-4 w-4"/>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <Button type="button" variant="outline" onClick={resetEventForm}>
                                        İptal
                                    </Button>
                                    <Button type="submit" className="bg-blue-600 text-white">
                                        {editingId ? 'Güncelle' : 'Kaydet'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}
                
                <div className="space-y-2">
                    {events.map(evt => (
                        <div 
                            key={evt.id} 
                            className="flex justify-between items-center p-4 border rounded-lg bg-white"
                        >
                            <div className="flex gap-4 items-center">
                                {evt.Image ? (
                                    <img 
                                        src={evt.Image} 
                                        alt="evt" 
                                        className="w-10 h-10 rounded object-cover border"
                                    />
                                ) : (
                                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                                        <Calendar className="text-slate-400 h-5 w-5"/>
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-bold">{evt.Title}</h3>
                                    <div className="flex gap-2 text-xs text-slate-500">
                                        <span>{evt.StartDate.split('T')[0]}</span> | 
                                        <span className="text-blue-600 font-bold ml-1">
                                            {evt.attendees_count || 0} Katılımcı
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleViewAttendees(evt.id, evt.Title)}
                                >
                                    <Users className="h-4 w-4 mr-1"/> Liste
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleEditEvent(evt)} 
                                    className="text-blue-500"
                                >
                                    <Edit className="h-4 w-4"/>
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteEvent(evt.id)} 
                                    className="text-red-500"
                                >
                                    <Trash2 className="h-4 w-4"/>
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}
